import React, { useState, useRef, useEffect } from "react";
import { sendChatToGroq } from "../lib/services/chatbotService";
import { FaComments, FaExpand, FaTimes } from "react-icons/fa";

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  const toggleChat = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    // Show greeting message when opening the chat
    if (newIsOpen && messages.length === 0) {
      setMessages([
        {
          role: "bot",
          content:
            "Hello! 👋 I'm your NITM Assistant.\nHow can I assist you with your property needs today?",
        },
      ]);
    }
  };

  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMessage = input;
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setInput("");

    try {
      const botReply = await sendChatToGroq(userMessage);
      setMessages((prev) => [...prev, { role: "bot", content: botReply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          content:
            "I'm currently unavailable. Please try again later or contact our support team.",
        },
      ]);
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <>
      {/* Floating icon on RIGHT side */}
      {!isOpen && (
        <div
          onClick={toggleChat}
          style={{
            position: "fixed",
            bottom: 80, // Increased from 20 to 80 to avoid bottom bar overlap
            right: 20,
            background: "#C5A995",
            color: "#fff",
            borderRadius: "50%",
            width: 60,
            height: 60,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            zIndex: 9999,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            transition: "transform 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = "scale(1)";
          }}
        >
          <FaComments size={26} />
        </div>
      )}

      {/* Chat window */}
      {isOpen && (
        <div
          style={{
            position: "fixed",
            bottom: 80, // Increased from 20 to 80 to avoid bottom bar overlap
            right: 20,
            width: isFullscreen ? "90vw" : 380,
            maxWidth: 600,
            height: isFullscreen ? "80vh" : 520,
            maxHeight: 700,
            background: "#F5EDE6",
            borderRadius: 20,
            boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
            display: "flex",
            flexDirection: "column",
            zIndex: 9999,
            overflow: "hidden",
            fontFamily: "Inter, sans-serif",
            border: "2px solid #DDC7BB80",
            animation: "slideIn 0.3s ease-out",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "#C5A995",
              color: "#fff",
              padding: "12px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            NITM Assistant
            <div>
              <FaExpand
                onClick={toggleFullscreen}
                style={{
                  cursor: "pointer",
                  marginRight: 12,
                  transition: "transform 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "scale(1.2)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "scale(1)";
                }}
              />
              <FaTimes
                onClick={toggleChat}
                style={{
                  cursor: "pointer",
                  transition: "transform 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "scale(1.2)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "scale(1)";
                }}
              />
            </div>
          </div>

          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              padding: 16,
              overflowY: "auto",
              background: "#FFF9F5",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                  maxWidth: "90%",
                  animation:
                    msg.role === "bot" && idx === 0
                      ? "fadeIn 0.5s ease"
                      : "none",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    padding: "10px 14px",
                    borderRadius: 16,
                    background: msg.role === "user" ? "#C5A995" : "#E8DCD3",
                    color: msg.role === "user" ? "#fff" : "#3A2E28",
                    wordBreak: "break-word",
                    whiteSpace: "pre-line", // This allows \n to create new lines
                    lineHeight: "1.4",
                  }}
                >
                  {msg.content}
                </span>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Field */}
          <div
            style={{
              padding: 10,
              display: "flex",
              gap: 8,
              borderTop: "1px solid #DDC7BB80",
              background: "#F5EDE6",
            }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about properties..."
              style={{
                flex: 1,
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid #DDC7BB80",
                fontSize: 14,
                outline: "none",
                background: "#fff",
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <button
              onClick={handleSend}
              style={{
                background: "#C5A995",
                color: "#fff",
                borderRadius: 12,
                padding: "10px 18px",
                border: "none",
                fontWeight: 500,
                cursor: "pointer",
                transition: "background 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#B89A87";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "#C5A995";
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>
        {`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(20px) scale(0.9);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </>
  );
};

export default Chatbot;
